import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Badge, Box, Flex, Grid, SimpleGrid, Text as MantineText, Tooltip } from "@mantine/core";
import { useForm } from "@mantine/form";
import { format, useMask } from "@react-input/mask";
import {
  IconArchive,
  IconCheck,
  IconClipboardPlus,
  IconMail,
  IconMessage,
  IconTrash,
} from "@tabler/icons-react";
import { updatedDiff } from "deep-object-diff";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  Button,
  CommentsModal,
  ConfirmModal,
  InputLabel,

  Text,
  TextInput,
  YesNoRadioButton,
} from "components";
import { IRootState } from "config/redux";
import { AppConstants, AuthorityConstants, RouteConstants, ServiceConstants } from "constant";
import NewTodoModal from "pages/todo/list/new";
import { UserService } from "services";
import { displayClasses } from "styles";
import { CreditResponse, UserResponse } from "types";
import { AddressUtils, AppUtils, StorageUtils, UserUtils } from "utils";

import QuickCheckButton from "./quick-check";

const { MASK_SWISS_PHONE_NUMBER, STORAGE_CHAT_USER } = AppConstants;
const { ROLE_USR_D, ROLE_QC_IND, ROLE_QC_CRUD, ROLE_TODO_CUD } = AuthorityConstants;
const { ROUTE_MY_CLIENTS, ROUTE_CLIENTS } = RouteConstants;
const { REQUEST_USERS_CLIENTS_DELETE_ID, REQUEST_CLIENT_POOL_ARCHIVE_ID } = ServiceConstants;

interface ClientDetailsProps {
  user: UserResponse;
  credit?: CreditResponse;
  agent: boolean;
  unreadMessages?: number | undefined;
}

export const ClientDetails = ({ agent, credit, unreadMessages, user }: ClientDetailsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const clientDeleteServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_USERS_CLIENTS_DELETE_ID],
  );
  const clientArchiveServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_CLIENT_POOL_ARCHIVE_ID],
  );
  const [archiveVisible, setArchiveVisible] = useState<boolean>(false);
  const [commentsVisible, setCommentsVisible] = useState<boolean>(false);
  const [deleteVisible, setDeleteVisible] = useState<boolean>(false);
  const [newTodoVisible, setNewTodoVisible] = useState<boolean>(false);
  const timeoutRefs = useRef<Map<string, any>>(new Map());
  const phoneNumberRef = useMask({
    mask: MASK_SWISS_PHONE_NUMBER,
    replacement: { _: /\d/ },
  });
  const hasCredit = !!credit;

  useEffect(() => {
    AppUtils.resolveServiceObject(
      clientDeleteServiceObject,
      REQUEST_USERS_CLIENTS_DELETE_ID,
      () => {
        setDeleteVisible(false);
        navigate(agent ? ROUTE_MY_CLIENTS : ROUTE_CLIENTS);
      },
    );
  }, [clientDeleteServiceObject, navigate, agent]);

  useEffect(() => {
    AppUtils.resolveServiceObject(
      clientArchiveServiceObject,
      REQUEST_CLIENT_POOL_ARCHIVE_ID,
      () => {
        setArchiveVisible(false);
        navigate(agent ? ROUTE_MY_CLIENTS : ROUTE_CLIENTS);
      },
    );
  }, [clientArchiveServiceObject, navigate, agent]);

  const update = useCallback(async (propertyName: string, propertyValue: any) => {
    await UserService.userClientUpdateProperty(user.id, propertyName, propertyValue);
    timeoutRefs.current.delete(propertyName);
  }, [user.id]);

  const updateCredit = useCallback(async (item: any) => {
    for (const propertyName in item) {
      if (timeoutRefs.current.has(propertyName)) {
        clearTimeout(timeoutRefs.current.get(propertyName));
      }
      timeoutRefs.current.set(
        propertyName,
        setTimeout(() => update(propertyName, item[propertyName]), 700),
      );
    }
  }, [update]);

  const form = useForm({
    mode: "uncontrolled",
    initialValues: { ...user, phoneNumber: format(user.phoneNumber, { mask: MASK_SWISS_PHONE_NUMBER, replacement: { _: /\d/ } }) },
    validateInputOnChange: true,
    validate: {
      houseNumber: (value: string | undefined) =>
        value && !/^[0-9]/.test(value.charAt(0)) ? t("user.address.houseNumber.startWith1") : null,
    },
    onValuesChange(values, previous) {
      const result = form.validate();
      if (result.hasErrors) {
        return;
      }
      updateCredit(updatedDiff(previous, values));
    },
  });

  const lastActivity = useMemo(() => {
    const lastActivity = user.lastActivity ?? undefined;
    if (lastActivity) {
      return new Date(lastActivity).toString();
    }
    return undefined;
  }, [user]);
  const showAppIndicator = user.source === "MOBILE";
  const sourceLabel = user.source
    ? t("source.options." + user.source)
    : "---";

  const renderDate = useCallback((pattern: string, date: string) => {
    const localizedDate = t(pattern, { date: new Date(date) });
    return localizedDate.replace(" ", "\xa0\xa0\xa0\xa0");
  }, [t]);

  const postalInputProps = form.getInputProps("postal");
  const todoClient = useMemo(() => ({
    id: user.id,
    fullName: user.fullName,
  }), [user.id, user.fullName]);

  const renderCompactDate = useCallback((pattern: string, date: string) => {
    const localizedDate = t(pattern, { date: new Date(date) });
    return localizedDate.replace(" ", "\xa0\xa0");
  }, [t]);

  return (
    <Box mb={"md"}>
      <Flex justify={"space-between"} align={"center"} className={displayClasses.header}>
        <Flex align={"center"}>
          <TextInput
            key={form.key("firstName")}
            {...form.getInputProps("firstName")}
            style={{
              fontWeight: "bold",
              margin: 0,
              marginRight: 8,
            }}
          />
          <TextInput
            key={form.key("lastName")}
            {...form.getInputProps("lastName")}
            style={{
              fontWeight: "bold",
              margin: 0,
              marginRight: 8,
            }}
          />
        </Flex>
        <Box>
          {UserUtils.hasAuthority(ROLE_TODO_CUD) && (
            <>
              <Tooltip label={t("todoList.new.title")} withArrow>
                <Button
                  variant={"transparent"}
                  size={"compact-xs"}
                  onClick={() => setNewTodoVisible(true)}
                >
                  <IconClipboardPlus color={"var(--mantine-color-gray-7)"} />
                </Button>
              </Tooltip>
              <NewTodoModal
                opened={newTodoVisible}
                onClose={() => setNewTodoVisible(false)}
                preselectedClient={todoClient}
                lockClient
              />
            </>
          )}
          {UserUtils.hasAnyAuthority([ROLE_QC_IND, ROLE_QC_CRUD]) && (
            <QuickCheckButton
              userId={user.id}
              blacklisted={user.blacklisted ?? false}
              isAdmin={UserUtils.hasAuthority(ROLE_QC_CRUD)}
            />
          )}
          <Button
            variant={"transparent"}
            size={"compact-xs"}
            onClick={() => {
              StorageUtils.setObject(STORAGE_CHAT_USER, user.id);
              navigate(`${RouteConstants.ROUTE_CHAT}`);
            }}
            style={{ overflow: "visible" }}
          >
            <IconMail color={"var(--mantine-color-gray-7)"} />
            {unreadMessages && unreadMessages > 0 ? (
              <Box
                style={{
                  position: "absolute",
                  top: -6,
                  right: 0,
                  display: "inline",
                  backgroundColor: "var(--mantine-color-gray-1)",
                }}
              >
                <Badge
                  size={"xs"}
                  bg={"var(--mantine-color-primary-6)"}
                  c={"var(--mantine-color-white)"}
                >
                  {unreadMessages}
                </Badge>
              </Box>
            ) : (
              <></>
            )}
          </Button>
          <>
            <Button
              variant={"transparent"}
              size={"compact-xs"}
              onClick={() => setCommentsVisible(true)}
            >
              <IconMessage color={"var(--mantine-color-gray-7)"} />
            </Button>
            <CommentsModal
              opened={commentsVisible}
              onClose={() => setCommentsVisible(false)}
              title={t("credit.comment.title")}
              userId={user.id}
            />
          </>
          {UserUtils.hasAuthority(ROLE_USR_D) && (
            <>
              <Tooltip label={t("clients.archive.title")} withArrow>
                <Button
                  variant={"transparent"}
                  size={"compact-xs"}
                  onClick={() => setArchiveVisible(true)}
                >
                  <IconArchive color={"var(--mantine-color-gray-7)"} />
                </Button>
              </Tooltip>
              <ConfirmModal
                opened={archiveVisible}
                requestId={REQUEST_CLIENT_POOL_ARCHIVE_ID}
                onSubmit={() => UserService.clientArchive(user.id)}
                onClose={setArchiveVisible}
                titleKey={"clients.archive.title"}
                descriptionKey={"clients.archive.description"}
                actionKey={"clients.archive.action"}
                t={t}
              />
              <Button
                variant={"transparent"}
                size={"compact-xs"}
                onClick={() => setDeleteVisible(true)}
              >
                <IconTrash color={"var(--mantine-color-error)"} />
              </Button>
              <ConfirmModal
                opened={deleteVisible}
                requestId={REQUEST_USERS_CLIENTS_DELETE_ID}
                onSubmit={() => UserService.userClientDelete(user.id)}
                onClose={setDeleteVisible}
                titleKey={"clients.delete.title"}
                descriptionKey={"clients.delete.description"}
                actionKey={"clients.delete.action"}
                t={t}
              />
            </>
          )}
        </Box>
      </Flex>
      <Box p={"md"}>
        <SimpleGrid cols={4}>
          <Box>
            <InputLabel label={t("user.phone.label")} />
            <Flex justify={"space-between"}>
              <TextInput
                key={form.key("countryCode")}
                {...form.getInputProps("countryCode")}
                w={"30%"}
                disabled={true}
              />
              <TextInput
                key={form.key("phoneNumber")}
                {...form.getInputProps("phoneNumber")}
                w={"65%"}
                disabled={hasCredit}
                ref={phoneNumberRef}
              />
            </Flex>
          </Box>
          <TextInput
            label={t("user.email.label")}
            key={form.key("email")}
            {...form.getInputProps("email")}
            disabled={hasCredit}
          />
          <Grid>
            <Grid.Col span={4}>
              <TextInput
                label={t("user.address.postal.label")}
                key={form.key("postal")}
                {...postalInputProps}
                disabled={hasCredit}
                maxLength={4}
                onChange={(event) => {
                  postalInputProps.onChange(event);

                  const city = AddressUtils.getCityFromPostal(event.currentTarget.value);
                  if (city) {
                    form.setFieldValue("city", city as any, { forceUpdate: true });
                  }
                }}
              />
            </Grid.Col>
            <Grid.Col span={8}>
              <TextInput
                label={t("user.address.city.label")}
                key={form.key("city")}
                {...form.getInputProps("city")}
                disabled={hasCredit}
              />
            </Grid.Col>
          </Grid>
          <Grid>
            <Grid.Col span={8}>
              <TextInput
                label={t("user.address.street.label")}
                key={form.key("street")}
                {...form.getInputProps("street")}
                disabled={hasCredit}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <TextInput
                label={t("user.address.houseNumber.label")}
                key={form.key("houseNumber")}
                {...form.getInputProps("houseNumber")}
                onBeforeInput={(event: any) => {
                  const cursorPos = event.currentTarget.selectionStart || 0;
                  if (cursorPos === 0) {
                    const inputValue = (event as any).data;
                    if (inputValue && !/^[0-9]/.test(inputValue)) {
                      event.preventDefault();
                    }
                  }
                }}
                disabled={hasCredit}
              />
            </Grid.Col>
          </Grid>
        </SimpleGrid>

        <SimpleGrid cols={4} mt={"md"}>
          <YesNoRadioButton
            label={t("user.hasCredit.label")}
            name={"hasCredit"}
            form={form}
            rounded={true}
          />
          <YesNoRadioButton
            label={t("user.hasCreditCard.label")}
            name={"hasCreditCard"}
            form={form}
            rounded={true}
          />
          <YesNoRadioButton
            label={t("user.hasLeasing.label")}
            name={"hasLeasing"}
            form={form}
            rounded={true}
          />
          <YesNoRadioButton
            label={t("user.planningCredit.label")}
            name={"planningCredit"}
            form={form}
            rounded={true}
          />
        </SimpleGrid>
      </Box>
      <Box pt={0}>
        <Box
          p={"md"}
          style={{
            backgroundColor: "var(--mantine-color-gray-1)",
          }}
        >
          <Grid gutter={"md"}>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Text c={"var(--mantine-color-gray-7)"} mb={"sm"}>
                {t("clients.date.activity", {
                  date: lastActivity ? renderDate("format.date_time2", lastActivity) : "---",
                })}
              </Text>
              <MantineText c={"var(--mantine-color-gray-7)"}>
                <span style={{ color: "var(--mantine-color-primary-6)" }}>
                  {t("clients.date.change.label")}
                  {":"}
                </span>{" "}
                {credit?.info?.modifiedAt
                  ? renderCompactDate("format.date_time2", credit.info.modifiedAt)
                  : "---"}
              </MantineText>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Text c={"var(--mantine-color-gray-7)"}>
                {t("source.title") + ": " + sourceLabel}
              </Text>
              {showAppIndicator && (
                <Flex align={"center"} gap={8} mt={"sm"}>
                  <Box
                    style={{
                      width: 18,
                      height: 18,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "var(--mantine-color-primary-6)",
                      borderRadius: 3,
                    }}
                  >
                    <IconCheck size={12} stroke={2.2} color="var(--mantine-color-white)" />
                  </Box>
                  <Text c={"var(--mantine-color-gray-7)"}>APP</Text>
                </Flex>
              )}
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Text c={"var(--mantine-color-gray-7)"}>
                {t("clients.date.registration", {
                  date: renderDate("format.date", user.info.createdAt),
                })}
              </Text>
            </Grid.Col>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};
